import React from 'react';
import { cn } from '@/lib/utils';
import { NAVY, withAlpha } from './mobileTheme';

/**
 * MobileNavTile — one professionally styled, directly-accessible navigation tile.
 * Accepts either a lucide icon component (`icon`) or an image URL (`iconSrc`).
 * Preserves routes/permissions: the parent decides `onClick` and whether to render.
 *
 * item: { key, label, icon?, iconSrc?, onClick, badge?, active?, soon?, accent?, testid? }
 */
export const MobileNavTile = ({ item, accent = NAVY }) => {
  const { label, icon: Icon, iconSrc, onClick, badge = 0, active = false, soon = false, disabled = false, testid } = item;
  const a = item.accent || accent;
  const tid = testid || `mnav-${item.key}`;
  return (
    <button
      type="button"
      data-testid={tid}
      onClick={onClick}
      disabled={disabled}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative flex min-h-[84px] flex-col items-center justify-center gap-2 rounded-2xl border px-2 py-3 text-center',
        'transition-[transform,box-shadow,border-color,background-color] duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
        'hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]',
        disabled && 'pointer-events-none opacity-45',
      )}
      style={{
        ['--tw-ring-color']: withAlpha(a, 0.45),
        borderColor: active ? a : 'rgba(226, 232, 240, 0.9)',
        borderWidth: active ? 1.5 : 1,
        background: active
          ? `linear-gradient(180deg, ${withAlpha(a, 0.10)} 0%, ${withAlpha(a, 0.045)} 100%)`
          : 'linear-gradient(180deg, #ffffff 0%, #f6f8fa 100%)',
        boxShadow: active ? `0 4px 14px ${withAlpha(a, 0.18)}` : '0 1px 2px rgba(16, 24, 40, 0.05)',
      }}
    >
      <span
        className="flex h-11 w-11 items-center justify-center rounded-xl"
        style={{
          background: active ? a : `linear-gradient(180deg, ${withAlpha(a, 0.14)} 0%, ${withAlpha(a, 0.07)} 100%)`,
          color: active ? '#ffffff' : a,
          boxShadow: active
            ? `inset 0 1px 0 rgba(255,255,255,0.25), 0 2px 6px ${withAlpha(a, 0.3)}`
            : `inset 0 0 0 1px ${withAlpha(a, 0.12)}`,
        }}
      >
        {iconSrc ? (
          <img src={iconSrc} alt="" width={24} height={24} className="h-6 w-6 object-contain" draggable={false} />
        ) : (
          Icon && <Icon size={22} strokeWidth={2} />
        )}
      </span>
      <span
        className={cn('block w-full break-words text-[11px] font-semibold leading-tight sm:text-xs', !active && 'text-[#0E2A47]')}
        style={active ? { color: a } : undefined}
      >
        {label}
      </span>
      {badge > 0 && (
        <span data-testid={`${tid}-badge`} className="absolute right-1.5 top-1.5 min-w-[18px] rounded-full bg-[#C8641E] px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {soon && !badge && (
        <span className="absolute right-1.5 top-1.5 rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-slate-600">soon</span>
      )}
    </button>
  );
};

/**
 * MobileNavGrid — directly-accessible icon grid for a module Overview/Dashboard.
 * Default three columns where width permits (spec 1.1). Intended for the mobile
 * branch of a module; desktop keeps its existing sidebar untouched.
 *
 * props: { items[], columns=3, accent, className, testid }
 */
export const MobileNavGrid = ({ items = [], columns = 3, accent = NAVY, className, testid = 'mobile-nav-grid' }) => (
  <div
    data-testid={testid}
    role="navigation"
    className={cn('grid', className)}
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: '0.625rem' }}
  >
    {items.map((it) => (
      <MobileNavTile key={it.key} item={it} accent={accent} />
    ))}
  </div>
);

export default MobileNavGrid;
